import { useCallback } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import {
	ContentAnalyticsViewedComments,
	type ContentAnalyticsViewedCommentsType,
} from '@confluence/inline-comments-queries';
export interface UseViewedCommentsResult {
	getViewedCommentsCacheOnly: () => {
		success: boolean;
		data?: Set<string>;
		error?: Error;
	};
	getViewedCommentsFresh: () => Promise<{ success: boolean; data?: Set<string>; error?: Error }>;
}

export const useViewedComments = ({ pageId }: { pageId: string }): UseViewedCommentsResult => {
	const loadViewedCommentsFromCache = useQueryInterop<ContentAnalyticsViewedCommentsType>(
		ContentAnalyticsViewedComments,
		{
			fetchPolicy: 'cache-only',
			variables: { contentId: pageId },
		},
	);

	// Network-only query for fresh data
	const loadViewedCommentsNetworkOnlyPromise = useQueryInterop<ContentAnalyticsViewedCommentsType>(
		ContentAnalyticsViewedComments,
		{
			fetchPolicy: 'network-only',
			variables: { contentId: pageId },
		},
	);

	// Helper function to process the query result
	const processQueryResult = useCallback((data: ContentAnalyticsViewedCommentsType) => {
		const commentIds = data?.contentAnalyticsViewedComments?.commentIds ?? [];
		const viewedCommentsSet = new Set(commentIds.filter((id): id is string => id !== null));

		return {
			success: true,
			data: viewedCommentsSet,
		};
	}, []);

	// Helper function for cache-only queries
	const executeCacheOnlyQuery = useCallback(() => {
		if (!pageId) {
			throw new Error('Page ID is required');
		}

		try {
			const { data, error } = loadViewedCommentsFromCache;

			if (data) {
				return processQueryResult(data);
			} else {
				return {
					success: false,
					error: error,
				};
			}
		} catch (error) {
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, processQueryResult, loadViewedCommentsFromCache]);

	// Helper function for network-only queries
	const executeNetworkOnlyQuery = useCallback(
		async (
			refetchFn: (variables?: any) => Promise<{ data: ContentAnalyticsViewedCommentsType }>,
		) => {
			if (!pageId) {
				throw new Error('Page ID is required');
			}

			try {
				const result = await refetchFn({ contentId: pageId });
				return processQueryResult(result.data);
			} catch (error) {
				return {
					success: false,
					error: error as Error,
				};
			}
		},
		[pageId, processQueryResult],
	);

	// Cached data query - uses cache-only for performance
	const getViewedCommentsCacheOnly = useCallback(() => {
		try {
			return executeCacheOnlyQuery();
		} catch (error) {
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [executeCacheOnlyQuery]);

	// Fresh data query - always uses network-only for latest data
	const getViewedCommentsFresh = useCallback(
		() => executeNetworkOnlyQuery(loadViewedCommentsNetworkOnlyPromise.refetch),
		[executeNetworkOnlyQuery, loadViewedCommentsNetworkOnlyPromise],
	);

	return {
		getViewedCommentsCacheOnly,
		getViewedCommentsFresh,
	};
};
